import { SetStateAction, useEffect, useState } from 'react';
import { Badge, Button, Card, Spinner, Tooltip } from 'flowbite-react';
import { AiFillCalendar, AiFillClockCircle } from 'react-icons/ai';
import { MdAttachFile } from 'react-icons/md';
import { BiLink } from 'react-icons/bi';
import Pagination from 'rc-pagination';
import { BsBookmarkCheckFill } from 'react-icons/bs';
import { Meeting, MeetingStatus, MentorFeedback, MentorFeedbackCreateInfo, UIMeeting } from 'types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { isScheduledOrOngoingMeeting } from 'shared-utils';
import MeetingRecordingModal from '../scorecard/MeetingRecordingModal';
import NPSmodal from './NPSmodal/NPSmodal';
import { useUserContext } from 'src/context/UserProvider';
import { Img } from 'react-image';

export enum Step {
	StepOne,
	StepTwo,
}

type Props = {
	data: UIMeeting[];
	meetingDataCategory?: string;
	total?: number;
};

const MentorMeetingCards = ({ data, total }: Props) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const page = Number(searchParams.get('page')) || 1;
	const [step, setStep] = useState<Step>(Step.StepOne);
	const [isEditable, setIsEditable] = useState<boolean>(true);

	const [isMeetingRecordingModalOpen, setIsMeetingRecordingModalOpen] = useState<boolean>(false);
	const [meetingId, setMeetingId] = useState<string>('');

	const [meetingJoiningId] = useState<string>('');
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);
	const [rating, setRating] = useState<number>(0);
	const [mentorFeedback, setMentorFeedback] = useState<MentorFeedbackCreateInfo>({
		score: 0,
		feedback: '',
		issuesFaced: [],
		improvements: '',
		wasScoreCalculatedCorrectly: '',
		predictedSalary: '',
		questionRelevance: '',
		candidateIntent: '',
	});
	const [meeting, setMeeting] = useState<Meeting>();
	const { user } = useUserContext();

	const handleJoinAsHost = (meetingId: string) => {
		navigate(`/mentors/interviews/live/${meetingId}`);
	};

	const onFeedbackModalOpen = (meeting: Meeting) => {
		setIsFeedbackModalOpen(true);
		setMeeting(meeting);
		setIsEditable(true);
	};

	const feedbackProvidedFn = (meeting: UIMeeting, mentorRating: MentorFeedback) => {
		setRating(mentorRating.score);
		setMentorFeedback({
			score: mentorRating.score,
			feedback: mentorRating.feedback,
			issuesFaced: mentorRating.issuesFaced,
			improvements: mentorRating.improvements,
			wasScoreCalculatedCorrectly: mentorRating.wasScoreCalculatedCorrectly,
			predictedSalary: mentorRating.predictedSalary,
			questionRelevance: mentorRating.questionRelevance,
			candidateIntent: mentorRating.candidateIntent,
		});

		setIsEditable(false);
		setIsFeedbackModalOpen(true);
		setMeeting(meeting);
	};

	const onFeedbackModalClose = () => {
		setIsFeedbackModalOpen(false);
		setMentorFeedback({
			score: 0,
			feedback: '',
			issuesFaced: [],
			improvements: '',
			wasScoreCalculatedCorrectly: '',
			predictedSalary: '',
			questionRelevance: '',
			candidateIntent: '',
		});
		setRating(0);
		setStep(Step.StepOne);
	};

	return (
		<>
			<div className="flex items-center justify-center mb-4">
				<Pagination
					total={total ?? 0}
					pageSize={15}
					current={page || 1}
					showSizeChanger={true}
					showTotal={total => `Showing ${(page - 1) * 15 + 1}-${page * 15} of ${total} meetings`}
					onChange={(p: SetStateAction<any>) => {
						setSearchParams({ page: p });
					}}
				></Pagination>
			</div>
			<div className="flex flex-col gap-4">
				{data.map((meeting, i) => (
					<div key={meeting._id}>
						<Card className="dark:bg-[#18222D] dark:!border-[#2c3844]">
							<div className="grid grid-cols-5 items-center justify-center gap-x-[14px]">
								<div className="flex flex-col justify-center gap-2 text-left overflow-hidden">
									<Tooltip content={meeting.title}>
										<p className="max-w-[260px] truncate text-xl">{meeting.title}</p>
									</Tooltip>
									{/* <p>Attempt 1</p> */}

									<div
										className="flex cursor-pointer gap-2 text-left"
										onClick={() => window.open(`/sample-assessment/${meeting._id}`, '_blank')}
									>
										<p>View Sample</p>
										<span className="mt-2 inline-block">
											<Img
												loading="lazy"
												src="/assets/externalLinkIcon.svg"
												alt="externalLinkIcon"
												width="12"
												height="12"
											/>
										</span>
									</div>
								</div>

								<div className="flex items-center justify-center">
									<div className="flex flex-col gap-4 whitespace-normal break-words md:min-w-[160px]">
										<p className="text-xl truncate max-w-[200px]">{meeting.participant?.name}</p>
										{meeting.participant?.profile?.resumeLink && (
											<>
												<Badge
													className="hidden w-max cursor-pointer items-center gap-[0px] pb-[3px] pl-[5px] pr-[5px] pt-[3px] hover:underline"
													color={'gray'}
													icon={MdAttachFile}
													onClick={() =>
														navigate(
															meeting?.participant?.profile?.resumeLink
																? meeting?.participant?.profile?.resumeLink
																: '',
														)
													}
												>
													resume
												</Badge>
											</>
										)}

										<Badge
											className="w-max cursor-pointer items-center gap-[0px] pb-[3px] pl-[5px] pr-[5px] pt-[3px] hover:underline"
											color={'gray'}
											icon={BiLink}
											onClick={() => window.open(`/profile/${meeting.participant.handler}`, '_blank')}
										>
											profile
										</Badge>
									</div>
								</div>
								<div className="flex items-center justify-center">
									<div className="flex flex-col gap-2">
										<div className="flex items-center gap-2">
											<AiFillCalendar size={'20px'} className="text-gray-400" />
											<div className="text-sm font-medium text-gray-400">
												{moment(meeting.startTime).format('DD MMM YYYY, hh:mm a')}
											</div>
										</div>
										<div className="flex items-center gap-2">
											<AiFillClockCircle size={'20px'} className="text-gray-400" />
											<div className="text-sm font-medium text-gray-400">{meeting.duration} mins</div>
										</div>
									</div>
								</div>
								<div className="flex items-center justify-center">
									<Badge className="max-w-fit" size="md" color="indigo">
										{meeting.meetingStatus}
									</Badge>
								</div>
								<div className="row-span-2 flex flex-col items-center justify-center gap-4">
									{meeting.meetingStatus === MeetingStatus.COMPLETED ? (
										<>
											<Button
												color="purplePlaced"
												data-testid={`interviews-mentor-meeting-card-${i}-button-view-recording`}
												onClick={() => {
													setMeetingId(meeting._id);
													setIsMeetingRecordingModalOpen(true);
												}}
											>
												View Recording
											</Button>

											{!meeting.mentorFeedback ? (
												<Badge
													className="m-auto w-max cursor-pointer items-center gap-[0px] pb-[3px] pl-[5px] pr-[5px] pt-[3px] hover:underline"
													color="failure"
													onClick={() => onFeedbackModalOpen(meeting)}
												>
													Provide Feedback
												</Badge>
											) : (
												<Badge
													className="m-auto w-max cursor-pointer items-center gap-[0px] pb-[3px] pl-[5px] pr-[5px] pt-[3px] hover:underline"
													color="success"
													icon={BsBookmarkCheckFill}
													onClick={() => {
														feedbackProvidedFn(meeting, meeting.mentorFeedback as MentorFeedback);
													}}
												>
													<span className="ml-1">Feedback Provided</span>
												</Badge>
											)}
										</>
									) : (
										isScheduledOrOngoingMeeting(meeting.meetingStatus) && (
											<>
												<Button
													color="purplePlaced"
													data-testid={`interviews-mentor-meeting-card-${i}-button-join`}
													onClick={() => handleJoinAsHost(meeting._id)}
													disabled={meetingJoiningId === meeting._id}
													className="px-[10px]"
												>
													{meetingJoiningId === meeting._id ? <Spinner size="sm" light={true} /> : 'Join'}
												</Button>
											</>
										)
									)}
								</div>
							</div>
						</Card>
					</div>
				))}

				<MeetingRecordingModal
					isOpen={isMeetingRecordingModalOpen}
					onClose={() => setIsMeetingRecordingModalOpen(false)}
					meetingId={meetingId}
				/>
				<NPSmodal
					rating={rating}
					mentorFeedback={mentorFeedback}
					setMentorFeedback={setMentorFeedback}
					setRating={setRating}
					isEditable={isEditable}
					meeting={meeting as UIMeeting}
					isOpen={isFeedbackModalOpen}
					onClose={onFeedbackModalClose}
					step={step}
					setStep={setStep}
					lastUnseenMeetingId={user?.flags?.lastUnseenMeetingId || ''}
				/>
			</div>
		</>
	);
};

export default MentorMeetingCards;
